import React, { useState, useEffect, useMemo } from 'react';
import CircularProgress from '../../common/CircularProgress';
import apiCoreAdapter from '../../../hooks/apiCoreAdapter';
import type { SessionWithPsychologist, Patient, WorkSchedule } from './data';
import { fetchPatientsOnce, fetchSessionsOnce, fetchSchedulesOnce } from './data';
import { isoToZoneParts, localTzToUTC } from './timeZoneHelpers';
import { timezoneToCountryCode } from '../../../utils/timezoneToCountry';
import CountryFlag from '../../common/CountryFlag';
import { addMinutesToTime, timeToMinutes, formatDayDdMmYyyy } from './utils';
import { buildStartTimeOptions } from './bookingUtils';
import { getServiceNameFromAppointmentType } from '../../SessionDebug';
import { getPaymentCountdown, type PaymentCountdownInfo } from '../../../utils/paymentCountdown';
import { BA_TIMEZONE, parseBackendSessionDate } from '../../../utils/timezone';
import { checkSessionAvailability, isMockSession } from '../../../utils/sessionAvailability';
import { ReportAbsenceButton } from '../../common/ReportAbsenceButton';
import { VariantWizard } from './VariantWizard';
import RescheduleModal from './RescheduleModal';
import RescheduleConfirmModal from './RescheduleConfirmModal';
import { RescheduleReasonModal } from './RescheduleReasonModal';
import { Clock, X, User, Calendar, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsPwaApp } from '../../../pwa/PwaModeContext';

import { activityTracker } from "../../../utils/activityTracker";
interface Slot {
  id: string;
  psychologist: string;
  slot: string;
  created_at: string;
  updated_at?: string;
  deleted_at?: string | null;
  appointment_type?: string | null;
  process_recurrence_id?: string | null;
}

type Props = {
  open: boolean;
  onClose: () => void;
  session: SessionWithPsychologist | null;
  onCancel: (sessionId: string, motiveId?: string, note?: string, initiatedBy?: string) => Promise<void>;
  onReschedule?: (
    sessionId: string,
    newDate: string,
    newTime: string,
    processId?: string,
    reason?: { motiveId?: string; note?: string; initiatedBy?: 'psychologist' | 'patient' }
  ) => Promise<void>;
  psychologistId?: string;
  loading?: boolean;
  onRefreshSession?: () => Promise<void>;
  onStartReschedule?: (session: SessionWithPsychologist) => void;
  therapistTimezone?: string;
  onViewProcess?: () => void;
  /** Vista inicial al abrir (deep-linking compartible): detalle o reprogramación */
  initialView?: 'details' | 'reschedule';
  /**
   * Solo lectura (app mobile): se ve la información de la sesión y "Ver proceso", sin ingresar al Meet, reportar
   * ausencia, cancelar ni reprogramar.
   */
  readOnly?: boolean;
};

// Helper function to convert nationality to ISO2 country code (fallback)
const nationalityToISO2 = (nationality?: string): string | null => {
  if (!nationality) return null;
  const mapping: Record<string, string> = {
    'AR': 'AR', 'Argentina': 'AR',
    'US': 'US', 'United States': 'US', 'USA': 'US',
    'MX': 'MX', 'Mexico': 'MX',
    'ES': 'ES', 'Spain': 'ES', 'España': 'ES',
    'CO': 'CO', 'Colombia': 'CO',
    'CL': 'CL', 'Chile': 'CL',
    'PE': 'PE', 'Peru': 'PE', 'Perú': 'PE',
    'BR': 'BR', 'Brazil': 'BR', 'Brasil': 'BR',
    'UY': 'UY', 'Uruguay': 'UY',
    'PY': 'PY', 'Paraguay': 'PY',
    'BO': 'BO', 'Bolivia': 'BO',
    'EC': 'EC', 'Ecuador': 'EC',
    'VE': 'VE', 'Venezuela': 'VE',
  };
  const upper = nationality.toUpperCase().trim();
  return mapping[upper] || mapping[nationality] || null;
};

// Helper function to get country code from patient (timezone first, nationality as fallback)
const getPatientCountryCode = (patient?: Patient | null): string | null => {
  if (!patient) return null;
  
  // Try timezone first (more accurate)
  const timezoneCode = timezoneToCountryCode(patient.timezone);
  if (timezoneCode) return timezoneCode;
  
  // Fallback to nationality if timezone is not available or cannot be mapped
  return nationalityToISO2(patient.nationality);
};

const SessionDetailsModal: React.FC<Props> = ({ open, onClose, session, onCancel, onReschedule, psychologistId, loading: externalLoading, onRefreshSession, onStartReschedule, therapistTimezone, onViewProcess, initialView, readOnly = false }) => {
  const { user } = useAuth();
  // App mobile: nada de "Ingresar a la sesión" ni Meet (solo desktop) — tampoco "Reportar ausencia", que es esperar al paciente en la sala.
  const isPwaApp = useIsPwaApp();
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelNote, setCancelNote] = useState<string>('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelWizardOpen, setCancelWizardOpen] = useState(false);
  const [tooSoonError, setTooSoonError] = useState(false);
  const [tooSoonRescheduleError, setTooSoonRescheduleError] = useState(false);
  const [viewMode, setViewMode] = useState<'details' | 'reschedule'>(initialView ?? 'details');
  const [openingMeet, setOpeningMeet] = useState(false);
  const [showMeetConfirm, setShowMeetConfirm] = useState(false);
  const [showMeetReminder, setShowMeetReminder] = useState(false);
  // Disponibilidad de la sesión contra el back (detecta cancelaciones/reprogramaciones
  // hechas en otra plataforma que siguen cacheadas localmente).
  // - sessionUnavailable: se setea por la verificación en segundo plano al abrir (oculta
  //   cancelar/reprogramar/ingresar de forma optimista) y por el chequeo al click de Ingresar.
  // - verifyingAvailability: spinner SOLO del click de "Ingresar" (abre el Meet sin pasar
  //   por el back, así que ahí la verificación es dura/bloqueante).
  const [verifyingAvailability, setVerifyingAvailability] = useState(false);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  // Removed unused countdown state to satisfy linter and avoid unnecessary renders
  
  // Reschedule states
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [existingSessions, setExistingSessions] = useState<SessionWithPsychologist[]>([]);
  // Corrimiento del inicio dentro del slot elegido (marcas de 5). El slot sigue saliendo de
  // la disponibilidad real de api-core; esto solo corre el arranque dentro de esa media hora.
  const [startOffsetMin, setStartOffsetMin] = useState<number>(0);
  const [workSchedules, setWorkSchedules] = useState<WorkSchedule[]>([]);
  // Pasos adicionales del flujo de reprogramación "fallback" (cuando no hay onStartReschedule,
  // ej. desde el tab de Sesiones): tras elegir slot en RescheduleModal, se confirma el
  // horario (antes/después) y se pide un motivo, igual que el flujo de la agenda semanal.
  const [confirmStepOpen, setConfirmStepOpen] = useState(false);
  const [reasonStepOpen, setReasonStepOpen] = useState(false);

  // Poll for session creation when status is "scheduled"
  useEffect(() => {
    if (!open || session?.status !== 'scheduled' || !onRefreshSession) return;
    const interval = setInterval(() => {
      onRefreshSession();
    }, 5000);
    return () => clearInterval(interval);
  }, [open, session?.status, onRefreshSession]);

  // Verificación optimista en segundo plano al abrir el modal: NO bloquea los botones
  // (no muestra spinner), pero si la sesión fue cancelada/reprogramada en otra plataforma
  // ocultamos las acciones (cancelar/reprogramar/ingresar) y mostramos el aviso.
  // Las sesiones mock (status 'scheduled' o id temporal) no se verifican.
  useEffect(() => {
    if (!open || !session) return;
    setSessionUnavailable(false);
    if (isMockSession(session.id, session.status)) return;

    let aborted = false;
    checkSessionAvailability(session.id, session.origISO || session.date)
      .then((result) => {
        if (aborted) return;
        if (result === 'unavailable') {
          setSessionUnavailable(true);
          setViewMode('details');
        }
      })
      .catch(() => {
        // Fail-open: no bloquear acciones ante un error de verificación.
      });

    return () => {
      aborted = true;
    };
    // Solo re-verificar al abrir o al cambiar de sesión (no en cada cambio del objeto session).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session?.id]);

  const formatDdMmYyyy = (dateStr?: string): string => formatDayDdMmYyyy(dateStr);

  // Fetch existing sessions excluding the current session being rescheduled
  // Reuses the same fetchSessionsOnce function that the calendar uses, which has caching
  const fetchExistingSessions = async (): Promise<SessionWithPsychologist[]> => {
    if (!psychologistId || !session) return [];

    try {
      // Use the same date range as fetchSessionsOnce (today - 6 days to today + 60 days)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - 6);
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + 60);
      
      // Generate weekKey similar to how WeeklyPsychologistCalendar does it
      const weekKey = `sessions-${startOfWeek.getTime()}-${endOfWeek.getTime()}`;
      
      // Use fetchSessionsOnce which has caching - it will return cached data if available
      // Use 'general' as specialty since we need all sessions regardless of specialty
      const allSessions = await fetchSessionsOnce(
        psychologistId,
        startOfWeek,
        endOfWeek,
        weekKey,
        'general'
      );

      // Exclude the current session being rescheduled
      const filteredSessions = allSessions.filter(s => s.id !== session.id);
      setExistingSessions(filteredSessions);
      return filteredSessions;
    } catch (error) {
      console.error('Error fetching existing sessions:', error);
      setExistingSessions([]);
      return [];
    }
  };

  // Check if a slot overlaps with existing sessions
  const checkSlotOverlapsWithSessions = (
    slot: Slot,
    sessionDuration: number,
    sessions: SessionWithPsychologist[]
  ): boolean => {
    if (sessions.length === 0) return false;

    const tz = therapistTimezone || BA_TIMEZONE;
    const slotParts = isoToZoneParts(slot.slot, tz);
    const slotDateStr = slotParts.date;
    const slotTimeStr = slotParts.time;

    // Calculate slot start and end in minutes
    const slotStartMin = timeToMinutes(slotTimeStr);
    const slotEndMin = slotStartMin + sessionDuration;

    // Check for overlap with any existing session on the same date
    return sessions.some(s => {
      const sParts = s.origISO ? isoToZoneParts(s.origISO, tz) : { date: s.date, time: s.time };
      if (sParts.date !== slotDateStr) return false;
      if (!(s.status === 'settled' || s.status === 'active' || s.status === 'incomplete')) {
        return false;
      }

      const sessionStartMin = timeToMinutes(sParts.time);
      const sessionDurationMinutes = s.duration || 60;
      const sessionEndMin = sessionStartMin + sessionDurationMinutes;

      // Check overlap: sessions overlap if startMin < slotEndMin && endMin > slotStartMin
      return sessionStartMin < slotEndMin && sessionEndMin > slotStartMin;
    });
  };

  // Check if a slot is within work schedule ranges
  const checkSlotWithinWorkSchedule = (
    slot: Slot,
    sessionDuration: number,
    schedules: WorkSchedule[]
  ): boolean => {
    if (schedules.length === 0) return false;

    const tz = therapistTimezone || BA_TIMEZONE;
    const slotParts = isoToZoneParts(slot.slot, tz);
    const slotDateStr = slotParts.date;
    const slotTimeStr = slotParts.time;
    
    // Calculate slot start and end in minutes
    const slotStartMin = timeToMinutes(slotTimeStr);
    const slotEndMin = slotStartMin + sessionDuration;
    
    // Get weekday from slot date (0 = Monday, 6 = Sunday)
    const [Y, M, D] = slotDateStr.split('-').map(Number);
    const slotDate = new Date(Y || 1970, (M || 1) - 1, D || 1);
    const dayOfWeek = slotDate.getDay(); // 0 = Sunday, 6 = Saturday
    // Convert to Monday = 0 format (0 = Monday, 6 = Sunday)
    const weekdayIdx = (dayOfWeek + 6) % 7;
    
    // Get active schedules for this weekday
    const daySchedules = schedules.filter(s => s.active && s.weekday === weekdayIdx);
    if (daySchedules.length === 0) return false;
    
    // Check if slot fits within any schedule range
    return daySchedules.some(schedule => {
      const scheduleStartMin = timeToMinutes(schedule.startTime);
      let scheduleEndMin = timeToMinutes(schedule.endTime);
      
      // Handle midnight wrap-around (00:00 means end of day)
      if (scheduleEndMin === 0 && schedule.endTime === '00:00') {
        scheduleEndMin = 24 * 60;
      }
      
      // Slot must be completely within the schedule range
      // slotStartMin >= scheduleStartMin && slotEndMin <= scheduleEndMin
      return slotStartMin >= scheduleStartMin && slotEndMin <= scheduleEndMin;
    });
  };

  const fetchSlots = async (
    sessionsToCheck: SessionWithPsychologist[] = existingSessions,
    schedulesToCheck: WorkSchedule[] = workSchedules
  ) => {
    if (!psychologistId) return;
    
    setLoadingSlots(true);
    setSlotsError(null);
    
    try {
      // Agregar timestamp para evitar caché y obtener datos frescos
      const timestamp = Date.now();
      const response = await apiCoreAdapter.psychologistFreeSlots(
        psychologistId,
        { _t: String(timestamp), is_reschedule: true, ...(session?.process_id && { process_id: session.process_id }) }
      );
      
      if (Array.isArray(response)) {
        // Filtrar slots que están al menos 6 horas en el futuro
        const now = Date.now();
        const sixHoursLater = now + (24 * 60 * 60 * 1000);
        
        const futureSlots = (response as Slot[]).filter(slot => {
          const slotTime = new Date(slot.slot).getTime();
          return slotTime >= sixHoursLater && !slot.deleted_at;
        });
        
        // Helper function para normalizar fecha/hora a minutos (ignorar segundos y milisegundos)
        const normalizeToMinutes = (isoString: string): number => {
          const date = new Date(isoString);
          return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes()
          ).getTime();
        };
        
        // IMPORTANTE: Eliminar duplicados basados en fecha y hora normalizada
        // En el modal de reagendamiento debemos mostrar solo slots únicos
        // para una mejor experiencia de usuario. Cada fecha/hora debe aparecer una sola vez.
        const uniqueSlots = futureSlots.reduce((acc, current) => {
          const currentNormalized = normalizeToMinutes(current.slot);
          const exists = acc.find(item => normalizeToMinutes(item.slot) === currentNormalized);
          
          if (!exists) {
            acc.push(current);
          }
          return acc;
        }, [] as Slot[]);
        
        // Ordenar por fecha ascendente
        uniqueSlots.sort((a, b) => new Date(a.slot).getTime() - new Date(b.slot).getTime());
        
        // Filtrar slots que se solapen con sesiones existentes y que estén dentro de rangos de trabajo
        const sessionDuration = session?.duration || 60;
        const availableSlots = uniqueSlots.filter(slot => {
          // Check overlap with existing sessions
          const hasOverlap = checkSlotOverlapsWithSessions(slot, sessionDuration, sessionsToCheck);
          if (hasOverlap) return false;
          
          // Check if slot is within work schedule ranges
          const withinSchedule = checkSlotWithinWorkSchedule(slot, sessionDuration, schedulesToCheck);
          return withinSchedule;
        });
        
        setSlots(availableSlots);
      } else {
        setSlots([]);
      }
    } catch (error) {
      setSlotsError(error instanceof Error ? error.message : 'Error al cargar slots');
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'reschedule' && psychologistId && session) {
      // Set loading state immediately when switching to reschedule mode
      setLoadingSlots(true);
      setSlotsError(null);
      
      // Fetch work schedules and existing sessions in parallel, then fetch slots
      Promise.all([
        fetchSchedulesOnce(psychologistId),
        fetchExistingSessions()
      ]).then(([schedules, sessions]) => {
        setWorkSchedules(schedules);
        fetchSlots(sessions, schedules);
      }).catch((error) => {
        console.error('Error fetching data:', error);
        setSlotsError('Error al cargar datos');
        setLoadingSlots(false);
      });
    } else if (viewMode !== 'reschedule') {
      // Reset loading state when not in reschedule mode
      setLoadingSlots(false);
      setSlots([]);
      setSlotsError(null);
      setWorkSchedules([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, psychologistId, session]);

  useEffect(() => {
    // Reset view mode when modal closes
    if (!open) {
      setViewMode('details');
      setSelectedSlot(null);
      setSlots([]);
      setSlotsError(null);
      setExistingSessions([]);
      setWorkSchedules([]);
      setLoadingSlots(false);
      setSessionUnavailable(false);
      setVerifyingAvailability(false);
      setConfirmStepOpen(false);
      setReasonStepOpen(false);
    } else if (open && psychologistId) {
      // Fetch patients when modal opens
      fetchPatientsOnce(psychologistId).then(setPatients).catch(console.error);
    }
  }, [open, psychologistId]);

  // Payment countdown — solo para sesiones con pago pendiente
  const isPendingPayment = open && session?.status === 'incomplete';
  const [paymentCountdown, setPaymentCountdown] = useState<PaymentCountdownInfo | null>(null);

  useEffect(() => {
    if (!isPendingPayment || !session) { setPaymentCountdown(null); return; }
    const tz = therapistTimezone || BA_TIMEZONE;
    const update = () => {
      const raw = session.origISO || `${session.date} ${session.time}:00`;
      const start = parseBackendSessionDate(raw, tz);
      setPaymentCountdown(getPaymentCountdown(start, tz));
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [isPendingPayment, session?.date, session?.time, session?.origISO, therapistTimezone]);

  // Al cambiar de slot se vuelve al horario sin corrimiento: el chip elegido para un slot
  // no debe arrastrarse al siguiente.
  useEffect(() => {
    setStartOffsetMin(0);
  }, [selectedSlot?.slot]);

  // Inicios en marcas de 5 sobre el slot elegido. Los bloqueos y las sesiones ya agendadas
  // deshabilitan el corrimiento que las pisaría: api-core no frena los bloqueos.
  const rescheduleStartTimeOptions = useMemo(() => {
    if (!selectedSlot) return undefined;
    const tz = therapistTimezone || BA_TIMEZONE;
    const parts = isoToZoneParts(selectedSlot.slot, tz);
    const busy = existingSessions
      .filter(s2 => s2.date === parts.date && s2.id !== session?.id)
      .filter(s2 => ['active', 'settled', 'incomplete', 'blocked', 'scheduled', 'free'].includes(s2.status))
      .map(s2 => {
        const startMin = timeToMinutes(s2.time);
        return { startMin, endMin: startMin + (s2.duration || 60) };
      });
    return buildStartTimeOptions({ slotStartTime: parts.time, durationMin: session?.duration || 60, busy });
  }, [selectedSlot, existingSessions, therapistTimezone, session?.duration, session?.id]);

  if (!open || !session) return null;

  const loading = externalLoading || cancelling;
  const isSettled = (session.status as unknown) === 'settled';

  // Regla: no permitir que la psicóloga cancele/reprograme la primera sesión del
  // paciente desde este portal (mismo criterio que el chip "Primera vez" en Sesiones).
  const isFirstSession = session.isFirst === true;

  // Regla: permitir cancelar solo si faltan al menos 6 horas
  const duration = session.duration || 60;
  const selectedTZ = therapistTimezone || BA_TIMEZONE;
  // `session.date`/`session.time` son hora de pared en el huso de la psicóloga (isoToZoneParts en las
  // 3 vistas), NO del navegador: `new Date(`${date}T${time}`)` los leía en el huso del dispositivo y
  // corría 6hs-de-cancelación, fin de sesión y la ventana de "Reportar ausencia" por la diferencia
  // entre ambos husos (ej. psicóloga en Buenos Aires con la compu en España: el botón aparecía 4hs antes).
  const sessionDateTime = localTzToUTC(session.date, session.time, selectedTZ);
  const hoursUntil = (sessionDateTime.getTime() - Date.now()) / 3600000;
  const canCancelByTimeWindow = hoursUntil >= 6;
  const sessionEndMs = sessionDateTime.getTime() + duration * 60000;
  const isSessionOver = Date.now() >= sessionEndMs;

  // Horarios calculados una sola vez para reutilizar en ambas vistas
  const toHHMM = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
  };
  const startMin = timeToMinutes(session.time);
  const psychStart = session.time;
  const psychEnd = toHHMM(startMin + duration);
  const patientByEmail = patients.find(p => p.email === session.patient.email);
  const patientData = patientByEmail || (session.process_id ? patients.find(p => p.process === session.process_id) : undefined);
  const patientTimezone = patientData?.timezone || session.patient.timezone || null;
  const countryCode = getPatientCountryCode(patientData || null);
  let patientStart: string | null = null;
  let patientEnd: string | null = null;
  if (patientTimezone) {
    try {
      const utcStart = localTzToUTC(session.date, session.time, selectedTZ);
      const utcEnd = new Date(utcStart.getTime() + duration * 60000);
      patientStart = isoToZoneParts(utcStart.toISOString(), patientTimezone).time;
      patientEnd = isoToZoneParts(utcEnd.toISOString(), patientTimezone).time;
    } catch { /* noop */ }
  }

  // Horario de referencia en Argentina (solo si el psicólogo está configurado en otro huso)
  let argStart: string | null = null;
  let argEnd: string | null = null;
  if (selectedTZ !== BA_TIMEZONE) {
    try {
      const utcStart = localTzToUTC(session.date, session.time, selectedTZ);
      const utcEnd = new Date(utcStart.getTime() + duration * 60000);
      argStart = isoToZoneParts(utcStart.toISOString(), BA_TIMEZONE).time;
      argEnd = isoToZoneParts(utcEnd.toISOString(), BA_TIMEZONE).time;
    } catch { /* noop */ }
  }

  const therapistIso2 = timezoneToCountryCode(selectedTZ);
  // Prefer timezone-derived ISO2 (covers session.patient.timezone fallback), then nationality fallback
  const patientIso2 = (patientTimezone ? timezoneToCountryCode(patientTimezone) : null) || countryCode;
  const patientCity = patientTimezone ? (patientTimezone.split('/').pop()?.replace(/_/g, ' ') ?? '') : '';
  const therapistCity = selectedTZ.split('/').pop()?.replace(/_/g, ' ') ?? '';

  // Horario del slot elegido en el fallback de reprogramación, para el paso de confirmación.
  let newSlotDate: string | null = null;
  let newSlotStart: string | null = null;
  /** Inicio del slot tal como lo ofrece la grilla, sin corrimiento — ancla de los chips. */
  let newSlotBaseStart: string | null = null;
  let newSlotEnd: string | null = null;
  let patientNewStart: string | null = null;
  let patientNewEnd: string | null = null;
  if (selectedSlot) {
    const slotParts = isoToZoneParts(selectedSlot.slot, selectedTZ);
    newSlotDate = slotParts.date;
    newSlotBaseStart = slotParts.time;
    newSlotStart = addMinutesToTime(slotParts.time, startOffsetMin);
    newSlotEnd = toHHMM(timeToMinutes(newSlotStart) + duration);
    if (patientTimezone) {
      try {
        const utcNewStart = new Date(new Date(selectedSlot.slot).getTime() + startOffsetMin * 60000);
        const utcNewEnd = new Date(utcNewStart.getTime() + duration * 60000);
        patientNewStart = isoToZoneParts(utcNewStart.toISOString(), patientTimezone).time;
        patientNewEnd = isoToZoneParts(utcNewEnd.toISOString(), patientTimezone).time;
      } catch { /* noop */ }
    }
  }

  const appointmentTypeId = (session as unknown as { appointment_type?: string }).appointment_type;
  const therapyName = (
    patientData?.processType
    || (appointmentTypeId ? getServiceNameFromAppointmentType(appointmentTypeId) : undefined)
    || (session as unknown as { therapyType?: string }).therapyType
    || 'Individual'
  );

  const handleOpenGoogleMeet = async () => {
    if (openingMeet) return;
    try {
      setOpeningMeet(true);
      const resp = await apiCoreAdapter.getExternalToolUrl('google meet');
      const url = (resp as unknown as { content?: string })?.content;
      if (url && typeof url === 'string') {
        const meetUrl = user?.email ? `${url}?authuser=${user.email}` : url;
        window.open(meetUrl, '_blank', 'noopener,noreferrer');
        activityTracker.trackAction('meet_opened');
      }
    } catch { /* noop */ }
    finally { setOpeningMeet(false); }
  };

  const handleJoinClick = async () => {
    if (verifyingAvailability) return;
    // Verificamos que la sesión siga viva antes de abrir el Meet (las mock se saltean).
    if (!isMockSession(session.id, session.status)) {
      try {
        setVerifyingAvailability(true);
        const availability = await checkSessionAvailability(session.id, session.origISO || session.date);
        if (availability === 'unavailable') {
          setSessionUnavailable(true);
          return;
        }
      } catch {
        // Fail-open ante errores: no bloquear el ingreso.
      } finally {
        setVerifyingAvailability(false);
      }
    }
    if (hoursUntil > 0) {
      setShowMeetConfirm(true);
    } else {
      setShowMeetReminder(true);
    }
  };

  const getTimeRemainingLabel = (): string => {
    const h = Math.floor(Math.max(0, hoursUntil));
    const m = Math.floor((Math.max(0, hoursUntil) - h) * 60);
    if (h > 0 && m > 0) return `${h} hora${h !== 1 ? 's' : ''} y ${m} minuto${m !== 1 ? 's' : ''}`;
    if (h > 0) return `${h} hora${h !== 1 ? 's' : ''}`;
    if (m > 0) return `${m} minuto${m !== 1 ? 's' : ''}`;
    return 'unos instantes';
  };

  const handleCancel = async () => {
    if (!canCancelByTimeWindow) {
      setTooSoonError(true);
      return;
    }
    setTooSoonError(false);
    setCancelWizardOpen(true);
  };

  const handleClose = () => {
    onClose();
    setShowCancelForm(false);
    setCancelReason('');
    setCancelNote('');
    setTooSoonError(false);
    setTooSoonRescheduleError(false);
    setViewMode('details');
    setSelectedSlot(null);
  };

  const handleOpenReschedule = () => {
    if (!canCancelByTimeWindow) {
      setTooSoonRescheduleError(true);
      return;
    }
    setTooSoonRescheduleError(false);
    // If calendar-based reschedule is available, use it
    if (onStartReschedule && session) {
      onStartReschedule(session);
      return;
    }
    // Fallback to internal reschedule view
    setViewMode('reschedule');
  };

  const handleBackToDetails = () => {
    setViewMode('details');
    setSelectedSlot(null);
    setTooSoonRescheduleError(false);
    setLoadingSlots(false);
    setSlots([]);
    setSlotsError(null);
    setConfirmStepOpen(false);
    setReasonStepOpen(false);
  };

  // Paso 1 del fallback: el usuario eligió un slot en RescheduleModal. En vez de reprogramar
  // directo, abrimos el paso de confirmación antes/después (igual que en la agenda semanal).
  const handleProceedToConfirmStep = () => {
    if (!selectedSlot) return;
    setConfirmStepOpen(true);
  };

  // Paso 3: ya con motivo/nota, se ejecuta la reprogramación real.
  const performReschedule = async (reason?: { motiveId?: string; note?: string; initiatedBy?: 'psychologist' | 'patient' }) => {
    if (!selectedSlot || !onReschedule) return;

    setRescheduling(true);
    try {
      const slotDate = new Date(selectedSlot.slot);

      // Formatear fecha y hora
      const dateStr = slotDate.toLocaleDateString('es-AR', {
        timeZone: therapistTimezone || BA_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).split('/').reverse().join('-');

      const timeStr = slotDate.toLocaleTimeString('es-AR', {
        timeZone: therapistTimezone || BA_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // Pasar process_id desde la sesión si está disponible
      // Con offset 0 esto es exactamente timeStr, el horario del slot de siempre.
      await onReschedule(session.id, dateStr, addMinutesToTime(timeStr, startOffsetMin), session.process_id, reason);
      setConfirmStepOpen(false);
      setReasonStepOpen(false);
      handleClose();
    } catch (error) {
      setSlotsError(error instanceof Error ? error.message : 'Error al reprogramar');
    } finally {
      setRescheduling(false);
    }
  };

  const canClick = !loading;

  // Si está abierto el wizard de cancelación, no renderizar el detalle ni overlay propio.
  if (cancelWizardOpen) {
    return (
      <>
        <VariantWizard
          isOpen={cancelWizardOpen}
          onClose={() => setCancelWizardOpen(false)}
          onConfirm={async (motiveId: string, note?: string, initiatedBy?: string) => {
            try {
              setCancelling(true);
              await onCancel(session.id, motiveId || undefined, note || undefined, initiatedBy);
              setCancelWizardOpen(false);
              onClose();
            } catch (error) {
              console.error('Error al cancelar sesión:', error);
            } finally {
              setCancelling(false);
            }
          }}
          session={{
            date: formatDdMmYyyy(session.date),
            time: psychStart,
            endTime: psychEnd,
            durationMinutes: duration,
            therapyType: therapyName,
            patientTime: patientStart && patientEnd ? `${patientStart} – ${patientEnd}` : undefined,
            patientTimezoneLabel: patientTimezone?.split('/').pop()?.replace(/_/g, ' '),
            patientTimezone: patientTimezone ?? undefined,
          }}
          patient={{
            name: session.patient.name || session.patient.email,
          }}
        />
      </>
    );
  }

  return (
    <>
      <div
        className="modal-overlay"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: open ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          direction: 'ltr',
          zIndex: 1000
        }}
        onClick={() => { if (!loading) handleClose(); } }
      >
        <div
          className="modal-content"
          style={{
            background: '#fff',
            borderRadius: '16px',
            width: '480px',
            maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {loading ? (
            <div style={{ padding: '3rem', display: 'grid', placeItems: 'center' }}>
              <CircularProgress sizeRem={2.5} label={cancelling ? "Cancelando sesión" : "Cargando"} />
            </div>
          ) : (
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-semibold text-foreground m-0">
                  {viewMode === 'reschedule' ? 'Reprogramar Sesión' : `Sesión con ${session.patient.name || session.patient.email}`}
                </h2>
                <button
                  onClick={() => { if (!loading) handleClose(); } }
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {viewMode === 'details' ? (
                <>
                  {/* Date/time card */}
                  <div className="p-5 rounded-xl border-2 border-primary/30 bg-white">
                    <p className="text-xs text-muted-foreground mb-1">Sesión agendada</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xl font-semibold text-foreground">{formatDdMmYyyy(session.date)}</p>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-primary/30 text-xs font-medium text-primary">
                        <Clock className="w-3.5 h-3.5" />
                        {duration} minutos
                      </span>
                    </div>
                    <div className="flex mt-4 border-t border-border/50 pt-4">
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-0.5">Tu horario</p>
                        <p className="text-sm font-medium text-foreground flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <span
                            data-testid="session-detail-therapist-time"
                            className="whitespace-nowrap"
                          >{psychStart} – {psychEnd}</span>
                          {(therapistCity || therapistIso2) && (
                            <span
                              data-testid="session-detail-therapist-city"
                              className="whitespace-nowrap text-xs text-muted-foreground"
                            >
                              {therapistCity && `· ${therapistCity}`}{therapistIso2 && <> <CountryFlag iso2={therapistIso2} size="0.75rem" /></>}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="w-px bg-border/50 mx-4" />
                      <div className="flex-1 flex flex-col">
                        <p className="text-xs text-muted-foreground mb-0.5">Horario Paciente</p>
                        {patientStart && patientEnd ? (
                          <div data-testid="session-detail-patient-time" className="flex flex-col flex-1">
                            <p className="flex flex-wrap items-center gap-x-1 gap-y-0.5 m-0">
                              <User className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                              <span className="whitespace-nowrap text-sm font-medium text-foreground">{patientStart} – {patientEnd}</span>
                            </p>
                            {patientCity && (
                              <div className="flex items-center gap-1 mt-auto pt-1">
                                <span
                                  data-testid="patient-city-label"
                                  className="whitespace-nowrap text-xs text-muted-foreground"
                                >
                                  {patientCity}{patientIso2 && <> <CountryFlag iso2={patientIso2} size="0.75rem" /></>}
                                </span>
                                <Tooltip delayDuration={150}>
                                  <TooltipTrigger asChild>
                                    <span
                                      data-testid="patient-tz-info-btn"
                                      className="inline-flex items-center flex-shrink-0 ml-auto cursor-default text-primary"
                                      aria-label="Sobre el huso horario del paciente"
                                    >
                                      <Info size={13} />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" sideOffset={6} className="max-w-[260px] text-xs leading-relaxed">
                                    Tené en cuenta que &quot;{patientCity}&quot; no necesariamente es donde vive el paciente, sino el huso horario que tiene configurado en su perfil.
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm font-medium text-foreground whitespace-nowrap">{psychStart} – {psychEnd}</p>
                        )}
                      </div>
                      {argStart && argEnd && (
                        <>
                          <div className="w-px bg-border/50 mx-4" />
                          <div className="flex-1 opacity-50">
                            <p className="text-xs text-muted-foreground mb-0.5">Horario Argentina</p>
                            <p data-testid="session-detail-arg-time" className="text-sm font-medium text-foreground whitespace-nowrap">{argStart} – {argEnd}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Session details */}
                  <div className="mt-5 space-y-3 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="w-4 h-4" />
                        <span>Paciente</span>
                      </div>
                      <span className="text-sm font-medium text-foreground">{session.patient.name || session.patient.email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>Tipo de cita</span>
                      </div>
                      <span className="text-sm font-medium text-foreground">{therapyName}</span>
                    </div>
                    {paymentCountdown && !paymentCountdown.isExpired && (
                      <div className={`flex items-center gap-1.5 pt-1 text-xs font-medium ${paymentCountdown.isUrgent ? 'text-orange-500' : 'text-amber-600'}`}>
                        <span className="flex-shrink-0">⏱</span>
                        <span data-testid="session-detail-payment-countdown" className="flex-1">{paymentCountdown.countdownLabel}</span>
                        <Tooltip delayDuration={150}>
                          <TooltipTrigger asChild>
                            <span
                              data-testid="payment-policy-info-btn"
                              className="inline-flex items-center flex-shrink-0 opacity-60 hover:opacity-100 cursor-default"
                              aria-label="Política de pago"
                            >
                              <Info size={12} />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={6} className="w-[280px] py-3 space-y-2 text-left leading-relaxed">
                            <p className="font-semibold text-foreground text-[0.8125rem]">Política de pago y cancelación</p>
                            <ul className="list-disc pl-4 space-y-1 text-xs">
                              <li>El pago debe realizarse hasta 12 horas antes del horario de la sesión.</li>
                              <li>Si no se paga en ese plazo, la sesión se cancela automáticamente.</li>
                              <li>Una sesión cancelada por falta de pago no puede realizarse el mismo día.</li>
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <RescheduleModal
                  isOpen
                  onClose={handleBackToDetails}
                  currentSession={{ date: formatDdMmYyyy(session.date), patient: session.patient.name || session.patient.email }}
                  slots={slots.map(s => ({ id: s.id, slot: s.slot }))}
                  therapistTimezone={selectedTZ}
                  patientTimezone={patientTimezone || undefined}
                  sessionDuration={duration}
                  selectedSlotId={selectedSlot?.id || null}
                  onSelectSlot={(slotId) => {
                    if (!slotId) { setSelectedSlot(null); return; }
                    const found = slots.find(s => s.id === slotId);
                    if (found) setSelectedSlot(found);
                  } }
                  onConfirm={handleProceedToConfirmStep}
                  confirming={rescheduling}
                  loading={loadingSlots}
                  error={slotsError}
                  patientCountryCode={countryCode} />
              )}

              {selectedSlot && newSlotDate && newSlotStart && newSlotEnd && (
                <RescheduleConfirmModal
                  open={confirmStepOpen}
                  onClose={() => setConfirmStepOpen(false)}
                  onConfirm={() => { setConfirmStepOpen(false); setReasonStepOpen(true); }}
                  confirming={rescheduling}
                  sessionInfo={{
                    patient: session.patient.name || session.patient.email,
                    currentDate: session.date,
                    currentTime: psychStart,
                    currentEndTime: psychEnd,
                    duration,
                    therapyType: therapyName,
                  }}
                  newDate={newSlotDate}
                  newTime={newSlotStart}
                  newEndTime={newSlotEnd}
                  startTimeOptions={rescheduleStartTimeOptions}
                  onSelectStartTime={(t) => setStartOffsetMin(timeToMinutes(t) - timeToMinutes(newSlotBaseStart || t))}
                  therapistTimezone={selectedTZ}
                  patientTimezone={patientTimezone}
                  patientCurrentTime={patientStart}
                  patientCurrentEndTime={patientEnd}
                  patientNewTime={patientNewStart}
                  patientNewEndTime={patientNewEnd}
                  patientCountryCode={countryCode}
                />
              )}

              <RescheduleReasonModal
                isOpen={reasonStepOpen}
                confirming={rescheduling}
                patientName={session.patient.name || session.patient.email}
                onClose={() => setReasonStepOpen(false)}
                onConfirm={(motiveId, note, initiatedBy) => {
                  performReschedule({ motiveId, note, initiatedBy });
                }}
              />

              {/* Mensajes cuando se intenta accionar fuera de ventana permitida */}
              {!showCancelForm && tooSoonError && (
                <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200 text-red-700 font-semibold text-sm">
                  No se puede cancelar una sesion cuando faltan menos de 6 horas para la misma.
                </div>
              )}
              {!showCancelForm && tooSoonRescheduleError && (
                <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200 text-red-700 font-semibold text-sm">
                  No se puede reprogramar una sesion cuando faltan menos de 6 horas para la misma.
                </div>
              )}
              {viewMode === 'details' && sessionUnavailable && (
                <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200 text-amber-800 font-medium text-sm">
                  Esta sesión ya no está disponible. Es posible que el paciente la haya cancelado o reprogramado desde otra plataforma.
                </div>
              )}

              {/* Formulario de cancelación */}
              {showCancelForm && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem !important', fontWeight: 700, color: '#991b1b' }}>Razón de cancelación</h4>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem !important', fontWeight: 700, color: '#374151' }}>Motivo</label>
                    <Select value={cancelReason} onValueChange={setCancelReason}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecciona un motivo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="patient_request">Solicitud del paciente</SelectItem>
                        <SelectItem value="rescheduled">Reprogramada</SelectItem>
                        <SelectItem value="no_show">No se presentó</SelectItem>
                        <SelectItem value="psychologist_request">Cancelación por psicólogo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem !important', fontWeight: 700, color: '#374151' }}>Nota</label>
                    <Textarea
                      value={cancelNote}
                      onChange={(e) => setCancelNote(e.target.value)}
                      placeholder="Agrega una nota sobre la cancelación..."
                      rows={3} />
                  </div>
                </div>
              )}

              {viewMode === 'details' && (onViewProcess || (!isSettled && !sessionUnavailable)) && (
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3 sm:gap-2 pt-4 border-t border-border">
                  {onViewProcess ? (
                    <Button
                      variant="ghost"
                      onClick={onViewProcess}
                      data-testid="session-detail-ver-proceso-btn"
                      className="text-[#6D57BC] hover:text-[#5a47a8] hover:bg-transparent px-0 self-start"
                    >
                      Ver proceso
                    </Button>
                  ) : <span />}
                  {!readOnly && <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 w-full sm:w-auto">
                      {!isPwaApp && (
                        <ReportAbsenceButton
                          sessionId={session.id}
                          patientName={session.patient.name || session.patient.email}
                          patientEmail={session.patient.email}
                          startMs={sessionDateTime.getTime()}
                          endMs={sessionEndMs}
                          className="w-full sm:w-auto"
                          processId={session.process_id}
                        />
                      )}
                      {session.status === "scheduled" ? (
                        <div className="flex items-center gap-2 text-muted-foreground font-semibold text-sm">
                          <div className="w-3.5 h-3.5 border-2 border-transparent border-t-gray-500 rounded-full animate-spin flex-shrink-0" />
                          Estamos creando la sesión, en breve podrás modificarla
                        </div>
                      ) : sessionUnavailable ? null : !canCancelByTimeWindow && !isSessionOver ? (
                        /* Próximas 6 horas hasta fin de sesión: solo botón de ingresar.
                           Verifica disponibilidad al click porque abre el Meet sin pasar por el back. */
                        isPwaApp ? (
                          // En la app la única acción de esta ventana era entrar al Meet (solo desktop): queda el aviso.
                          <p className="m-0 text-sm text-muted-foreground" data-testid="session-detail-too-soon-note">
                            Faltando menos de 6 horas ya no se puede cancelar ni reprogramar la sesión.
                          </p>
                        ) : verifyingAvailability ? (
                          <div className="flex items-center gap-2 text-muted-foreground font-semibold text-sm">
                            <div className="w-3.5 h-3.5 border-2 border-transparent border-t-gray-500 rounded-full animate-spin flex-shrink-0" />
                            Verificando disponibilidad…
                          </div>
                        ) : (
                        <>
                          {showMeetConfirm ? (
                            <div className="w-full p-4 bg-violet-50 rounded-lg border border-violet-200">
                              <p className="text-sm text-foreground mb-3">
                                Tu sesión comienza en <strong>{getTimeRemainingLabel()}</strong>. ¿Querés ingresar al meet ahora?
                              </p>
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => setShowMeetConfirm(false)}>
                                  Volver
                                </Button>
                                <Button
                                  size="sm"
                                  disabled={openingMeet}
                                  onClick={() => { setShowMeetConfirm(false); setShowMeetReminder(true); } }
                                  className="bg-[#6D57BC] text-white hover:bg-[#5a47a8]"
                                >
                                  Sí, ingresar
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              disabled={openingMeet}
                              onClick={handleJoinClick}
                              className="w-full sm:w-auto bg-[#6D57BC] text-white hover:bg-[#5a47a8]"
                            >
                              {openingMeet ? 'Cargando...' : 'Ingresar a la sesión'}
                            </Button>
                          )}
                        </>
                        )
                      ) : !isSettled ? (
                        /* Más de 6 horas: cancelar y reprogramar. Optimistas (se muestran sin
                           esperar la verificación); el chequeo en segundo plano, si la sesión ya
                           no está disponible, las oculta vía sessionUnavailable (arriba). Gateado
                           por !isSettled: una sesión ya pagada/liquidada no se cancela/reprograma
                           desde acá — a diferencia de "Reportar ausencia" e "Ingresar a la sesión",
                           que sí tienen sentido aunque ya esté pagada. */
                        <>
                          {isFirstSession ? (
                            <Tooltip delayDuration={150}>
                              <TooltipTrigger asChild>
                                <span className="inline-flex" tabIndex={0} data-testid="session-detail-cancel-tooltip-trigger">
                                  <Button
                                    variant="ghost"
                                    disabled
                                    data-testid="session-detail-cancel-btn"
                                    className="text-red-700 hover:text-red-800 hover:bg-red-50"
                                  >
                                    Cancelar sesión
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                No se puede cancelar la primera sesión del paciente.
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Button
                              variant="ghost"
                              disabled={!canClick}
                              onClick={handleCancel}
                              data-testid="session-detail-cancel-btn"
                              className="text-red-700 hover:text-red-800 hover:bg-red-50"
                            >
                              {showCancelForm ? 'Confirmar cancelación' : 'Cancelar sesión'}
                            </Button>
                          )}
                          {!showCancelForm && (
                            isFirstSession ? (
                              <Tooltip delayDuration={150}>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex" tabIndex={0} data-testid="session-detail-reschedule-tooltip-trigger">
                                    <Button variant="outline" disabled data-testid="session-detail-reschedule-btn">
                                      Reprogramar
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  No se puede reprogramar la primera sesión del paciente.
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Button
                                variant="outline"
                                disabled={loading}
                                onClick={handleOpenReschedule}
                                data-testid="session-detail-reschedule-btn"
                              >
                                Reprogramar
                              </Button>
                            )
                          )}
                        </>
                      ) : null}
                  </div>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>{!isPwaApp && <Dialog open={showMeetReminder} onOpenChange={setShowMeetReminder}>
          <DialogContent style={{ maxWidth: '400px', padding: '1.5rem' }}>
            <DialogHeader>
              <DialogTitle>Antes de ingresar</DialogTitle>
            </DialogHeader>
            <div className="mt-1 mb-4">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Recordatorio:</strong> asegurate de estar logueada en Google Meet con tu cuenta de <strong>@psimammoliti</strong> para evitar errores al ingresar.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowMeetReminder(false)}>
                Volver
              </Button>
              <Button
                disabled={openingMeet}
                onClick={() => { setShowMeetReminder(false); handleOpenGoogleMeet(); } }
                className="bg-[#6D57BC] text-white hover:bg-[#5a47a8]"
              >
                {openingMeet ? 'Cargando...' : 'Ingresar al Meet'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>}
      </>
  );
};

export default SessionDetailsModal;

